import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { FaTrashAlt } from "react-icons/fa";
import axios from "axios";

// Dummy images for recently viewed (optional)
// import Pr1 from "../../../assets/images/pr1.webp";
// import Pr2 from "../../../assets/images/pr2.webp";
// import Pr3 from "../../../assets/images/pr3.webp";
// import Pr4 from "../../../assets/images/tshirt.png";
// import Pr5 from "../../../assets/images/jeans.jpg";

// ✅ Import Context
import { useMenu } from "../../context/MenuContext";

const API_URL = "https://code.gurusport.in/api/cart";

const CartDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  // const recentlyViewed = [Pr1, Pr2, Pr3, Pr4, Pr5];

  // ✅ Use global cart count manager
  const { fetchMenuCounts = () => {}, setCartCount = () => {} } =
    useMenu() || {};

  // Tokens
  const authToken = localStorage.getItem("authToken");
  const guestToken = localStorage.getItem("guest_token");
  const userId =
    localStorage.getItem("authUserId") || localStorage.getItem("guest_id");
  const token = authToken || guestToken;
  const isGuest = !authToken && !!guestToken;

  // ✅ Fetch Cart
  const fetchCart = async () => {
    try {
      if (!token) return;

      const url = isGuest ? `${API_URL}/?guest_token=${guestToken}` : API_URL;

      const res = await axios.get(url, {
        headers: isGuest
          ? { "Content-Type": "application/json" }
          : { Authorization: `Bearer ${authToken}` },
      });

      if (
        res.data &&
        Array.isArray(res.data.data) &&
        res.data.data.length > 0
      ) {
        const mergedItems = res.data.data.flatMap((cart) => cart.items || []);
        const withQty = mergedItems.map((item) => ({
          ...item,
          quantity: item.quantity || 1,
        }));
        setCartItems(withQty);

        // ✅ update count globally
        const totalCount = withQty.reduce(
          (sum, item) => sum + item.quantity,
          0
        );
        setCartCount(totalCount);
      } else {
        setCartItems([]);
        setCartCount(0);
      }

      fetchMenuCounts(userId);
    } catch (error) {
      console.error("❌ Error fetching cart:", error);
    }
  };

  // ✅ Increase Quantity
  const handleIncrease = async (id) => {
    const item = cartItems.find((item) => item.id === id);
    if (!item) return;
    const newQuantity = item.quantity + 1;

    try {
      const url = isGuest
        ? `${API_URL}/${id}?guest_token=${guestToken}`
        : `${API_URL}/${id}`;

      await axios.put(
        url,
        { product_id: id, quantity: newQuantity },
        {
          headers: isGuest
            ? { "Content-Type": "application/json" }
            : { Authorization: `Bearer ${authToken}` },
        }
      );

      const updated = cartItems.map((i) =>
        i.id === id ? { ...i, quantity: newQuantity } : i
      );
      setCartItems(updated);

      // ✅ Update global cart count
      const totalCount = updated.reduce((sum, i) => sum + i.quantity, 0);
      setCartCount(totalCount);

      fetchMenuCounts(userId);
    } catch (error) {
      console.error("Error increasing quantity:", error);
    }
  };

  // ✅ Decrease Quantity
  const handleDecrease = async (id) => {
    const item = cartItems.find((item) => item.id === id);
    if (!item || item.quantity <= 1) return;
    const newQuantity = item.quantity - 1;

    try {
      const url = isGuest
        ? `${API_URL}/${id}?guest_token=${guestToken}`
        : `${API_URL}/${id}`;

      await axios.put(
        url,
        { product_id: id, quantity: newQuantity },
        {
          headers: isGuest
            ? { "Content-Type": "application/json" }
            : { Authorization: `Bearer ${authToken}` },
        }
      );

      const updated = cartItems.map((i) =>
        i.id === id ? { ...i, quantity: newQuantity } : i
      );
      setCartItems(updated);

      // ✅ Update global cart count
      const totalCount = updated.reduce((sum, i) => sum + i.quantity, 0);
      setCartCount(totalCount);

      fetchMenuCounts(userId);
    } catch (error) {
      console.error("Error decreasing quantity:", error);
    }
  };

  // ✅ Delete Item
  const handleRemoveItem = async (id) => {
    try {
      const url = isGuest
        ? `${API_URL}/${id}?guest_token=${guestToken}`
        : `${API_URL}/${id}`;

      await axios.delete(url, {
        headers: isGuest
          ? { "Content-Type": "application/json" }
          : { Authorization: `Bearer ${authToken}` },
      });

      const updated = cartItems.filter((item) => item.id !== id);
      setCartItems(updated);

      // ✅ Update global cart count
      const totalCount = updated.reduce((sum, i) => sum + i.quantity, 0);
      setCartCount(totalCount);

      fetchMenuCounts(userId);
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  // ✅ Subtotal
  const subtotal = cartItems.reduce((total, item) => {
    const price =
      typeof item.price === "object"
        ? parseFloat(item.price.amount || 0)
        : parseFloat(item.price || 0);
    return total + price * item.quantity;
  }, 0);

  useEffect(() => {
    if (isOpen) fetchCart();
  }, [isOpen]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40"
          onClick={onClose}
        ></div>
      )}

      <div
        className={`fixed top-0 right-0 h-[80vh] w-full sm:w-[350px] md:w-[400px] lg:w-[430px] bg-white z-50 shadow-lg transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="font-bold text-sm uppercase">
              Shopping cart ({cartItems.length})
            </h2>
            <button onClick={onClose}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Cart Items */}
          <div className="p-4 space-y-4">
            {cartItems.length > 0 ? (
              cartItems.map((item) => {
                const productName = item.product?.name || "Unnamed Product";
                const productImage = item.product?.default_image || "#";
                const price =
                  typeof item.price === "object"
                    ? parseFloat(item.price.amount || 0)
                    : parseFloat(item.price || 0);

                return (
                  <div key={item.id} className="flex gap-4 border-b pb-3">
                    {/* <img
                      src={productImage}
                      alt={productName}
                      className="w-[80px] h-[100px] object-cover rounded"
                    /> */}
                    <img
                      src={productImage}
                      alt={productName}
                      className="w-[60px] h-[80px] sm:w-[70px] sm:h-[90px] md:w-[80px] md:h-[100px] object-cover rounded"
                    />
                    <div className="flex-1 text-sm">
                      {/* <h4 className="font-semibold">{productName}</h4> */}
                      <h4 className="font-semibold text-sm sm:text-base md:text-base">
                        {productName}
                      </h4>

                      {item.size && (
                        <p className="text-gray-500">Size: {item.size?.name}</p>
                      )}
                      {item.color && (
                        <p className="text-gray-500">
                          Color: {item.color?.name}
                        </p>
                      )}

                      <div className="flex justify-between items-center mt-2">
                        {/* <span
                          className="font-semibold"
                          style={{ fontSize: "18.5px" }}
                        >
                          ₹{(price * item.quantity).toFixed(2)}
                        </span> */}
                        <span className="font-semibold text-sm sm:text-base md:text-lg">
                          ₹{(price * item.quantity).toFixed(2)}
                        </span>
                        {/* <div
                          className="flex items-center gap-2 border rounded-2xl px-2 py-1 bg-gray-200"
                          style={{ marginRight: "-30px" }}
                        > */}
                        <div className="flex items-center gap-2 border rounded-2xl px-2 py-1 bg-gray-200 -mr-2 sm:-mr-4 md:-mr-6">
                          <button
                            className="text-lg px-2"
                            onClick={() => handleDecrease(item.id)}
                          >
                            −
                          </button>
                          <span>{item.quantity}</span>
                          <button
                            className="text-lg px-2"
                            onClick={() => handleIncrease(item.id)}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      className="self-start mt-2 sm:mt-0"
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      <FaTrashAlt className="text-red-600 hover:text-red-800" />
                    </button>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-gray-500">Your cart is empty</p>
            )}
          </div>

          {/* Summary & Actions */}
          <div className="p-4 border-t text-sm pb-6 mt-auto">
            <div className="flex justify-between mb-4">
              <span>{cartItems.length} items</span>
              <span className="font-bold text-red-600">
                ₹{subtotal.toFixed(2)}
              </span>
            </div>

            <div className="flex gap-3">
              <button
                className="w-1/2 bg-red-600 text-white py-2 rounded font-semibold hover:bg-red-700"
                onClick={() => {
                  navigate("/payment");
                  onClose();
                }}
              >
                Checkout
              </button>
              <button
                className="w-1/2 border border-gray-300 py-2 rounded font-semibold hover:bg-gray-100"
                onClick={() => {
                  navigate("/cart");
                  onClose();
                }}
              >
                View Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CartDrawer;
